import { AxiosPromise } from 'axios';
import { URLS } from '@/services/Constants';
import { commonAxios as axios } from '@/services/Authentication';
import { AssignableUser } from '@/models/AssignableUser';
import { User } from '@/models/User';
import FaxUserService from '@/services/FaxUserService';
import SupportUserService from '@/services/SupportUserService';
import { FunctionKey } from '@/models/FunctionKey';
import { BLFuser } from '@/models/BLFuser';
import { BLFgroup } from '@/models/BLFgroup';
import { AssignableBLFuser } from '@/models/AssignableBLFuser';
import { GroupLoginGroupSetting } from '@/models/GroupLoginGroupSetting';

export default {

    // GET
    getAllFunctionKeySettingsForUser(accountId: number): AxiosPromise<any> {
        return axios().request({
            url: accountId !== 1000 ? `${URLS().urlFunctionKeySets}?actOnBehalfOf=${accountId}` : URLS().urlFunctionKeySets,
            method: 'get',
        });
    },

    getFunctionKeySets(): AxiosPromise<any> {
        return axios().request({
            url: URLS().urlFunctionKeySetsEdit,
            method: 'get',
        });
    },

    // POST
    setButtonForMailBoxConfiguration(accountId: number): AxiosPromise<any> {
        return axios().request({
            url: accountId !== 1000 ? `${URLS().urlFunctionKeySets}?actOnBehalfOf=${accountId}` : URLS().urlFunctionKeySets,
            method: 'post',
            data: {
                functionKeyType: 'QUICKDIAL',
                accountId,
                name: 'Meine Mailbox',
                position: 0,
                directCallTargetnumber: '*9'
            }
        });
    },

    setBLFforSpecificUser(accountId: number, name: string, targetId: number): AxiosPromise<any> {
        return axios().request({
            url: accountId !== 1000 ? `${URLS().urlFunctionKeySets}?actOnBehalfOf=${accountId}` : URLS().urlFunctionKeySets,
            method: 'post',
            data: {
                functionKeyType: 'BUSYLAMPFIELD',
                accountId,
                name,
                blfAccountId: targetId
            }
        });
    },

    setBLFforSpecificGroup(accountId: number, group: BLFgroup, position: number | null = null, isUserGroupId: boolean = false): AxiosPromise<any> {
        return axios().request({
            url: accountId !== 1000 ? `${URLS().urlFunctionKeySets}?actOnBehalfOf=${accountId}` : URLS().urlFunctionKeySets,
            method: 'post',
            data: {
                functionKeyType: 'GROUPLOGIN',
                accountId,
                name: group.name,
                groupIds: [group.id],
                position
            }
        }).then(functionkey => {
            if (isUserGroupId) {
                return new Promise<any>((resolve, reject) => resolve(functionkey));
            }
            return axios().request({
                url: `${URLS().urlFunctionKeySets}/edit/${functionkey.data.id}?actOnBehalfOf=${accountId}`,
                method: 'get'
            }).then(editData => {
                const groupLoginGroupSettings: GroupLoginGroupSetting[] = editData.data.editFunctionKeyGroupLogin.editFunctionKeyGlGroupSettings;
                const groupIDforFunctionKey = groupLoginGroupSettings.filter(edit => edit.groupname === group.name)[0];
                if (groupIDforFunctionKey == null) {
                    return new Promise<any>((resolve, reject) => resolve(functionkey));
                }
                return axios().request({
                    url: accountId !== 1000 ?
                        `${URLS().urlFunctionKeySets}/${functionkey.data.id}?actOnBehalfOf=${accountId}` :
                        `${URLS().urlFunctionKeySets + '/' + functionkey.data.id}`,
                    method: 'put',
                    data: {
                        functionKeyType: 'GROUPLOGIN',
                        accountId,
                        name: group.name,
                        groupIds: [groupIDforFunctionKey.groupId],
                        position: functionkey.data.position
                    }
                });

            });
        });
    },

    // PUT
    updateBLFforSpecificUser(accountId: number, targetUser: BLFuser, functionKeyId: number): AxiosPromise<any> {
        return axios().request({
            url: accountId !== 1000 ?
                `${URLS().urlFunctionKeySets}/${functionKeyId}?actOnBehalfOf=${accountId}` :
                `${URLS().urlFunctionKeySets + '/' + functionKeyId}`,
            method: 'put',
            data: {
                functionKeyType: 'BUSYLAMPFIELD',
                id: functionKeyId,
                accountId,
                name: `${targetUser.firstName} ${targetUser.familyName}`,
                blfAccountId: targetUser.id
            }
        });
    },

    updateBLFforSpecificGroup(accountId: number, group: BLFgroup, functionKey: FunctionKey): AxiosPromise<any> {
        return this.deleteBLFforSpecificUser(accountId, functionKey.id).then(response => {
            return this.setBLFforSpecificGroup(accountId, group, functionKey.position, true);
        });
    },

    // DELETE
    deleteBLFforSpecificUser(accountId: number, functionKeyId: number): AxiosPromise<any> {
        return axios().request({
            url: accountId !== 1000 ?
                `${URLS().urlFunctionKeySets}/${functionKeyId}?actOnBehalfOf=${accountId}` :
                `${URLS().urlFunctionKeySets + '/' + functionKeyId}`,
            method: 'delete',
        });
    },

    // PREPROCESS
    async setBLFforAllUsers(newUser: User, userList: User[]) {
        SupportUserService._removeSupportUserFromList(userList);
        FaxUserService._removeFaxUserFromList(userList);
        for (const user of userList) {
            if (newUser.id === user.id) {
                continue;
            }
            await this.setBLFforSpecificUser(
                user.id,
                `${newUser.firstName} ${newUser.familyName}`,
                newUser.id
            ).then(async response => {
                await this.setBLFforSpecificUser(
                    newUser.id,
                    `${user.firstName} ${user.familyName}`,
                    user.id
                );
            });
        }
    },

    updateBLFforAllUsers(updatedUser: BLFuser, userList: User[]) {
        SupportUserService._removeSupportUserFromList(userList);
        FaxUserService._removeFaxUserFromList(userList);
        for (const user of userList) {
            if (updatedUser.id === user.id) {
                continue;
            }
            this.getAllFunctionKeySettingsForUser(user.id)
                .then(response => {
                    const userKeys: FunctionKey[] = response.data;
                    const filteredFunctionKeyArray = userKeys.filter(key => key.functionKeyType === 'BUSYLAMPFIELD' && key.blfAccountId === updatedUser.id);
                    if (filteredFunctionKeyArray.length > 0) {
                        this.updateBLFforSpecificUser(user.id, updatedUser, filteredFunctionKeyArray[0].id);
                    }
                });
        }
    },

    deleteBLFforAllUsers(deletedUserId: number, userList: User[]) {
        SupportUserService._removeSupportUserFromList(userList);
        FaxUserService._removeFaxUserFromList(userList);
        for (const user of userList) {
            if (deletedUserId === user.id) {
                continue;
            }
            this.getAllFunctionKeySettingsForUser(user.id)
                .then(response => {
                    const userKeys: FunctionKey[] = response.data;
                    const filteredFunctionKeyArray = userKeys.filter(key => key.functionKeyType === 'BUSYLAMPFIELD' && (key.blfAccountId === deletedUserId || key.blfAccountId === -1 || key.blfAccountId === null));
                    if (filteredFunctionKeyArray.length > 0) {
                        this.deleteBLFforSpecificUser(user.id, filteredFunctionKeyArray[0].id);
                    }
                });
        }
    },

    setBLFforSpecificGroupForAllUsers(groupName: string, userList: AssignableUser[]) {
        SupportUserService._removeSupportUserFromAssignableUsersList(userList);
        FaxUserService._removeFaxUserFromAssignableUsersList(userList);
        userList = userList.filter(user => user.assigned);
        this.getFunctionKeySets().then(response => {
            const groupLoginGroupSettings: GroupLoginGroupSetting[] = response.data.editFunctionKeyGroupLogin.editFunctionKeyGlGroupSettings;
            const groupIDforFunctionKey = groupLoginGroupSettings.filter(group => group.groupname === groupName)[0];
            if (groupIDforFunctionKey != null) {
                for (const user of userList) {
                    this.setBLFforSpecificGroup(user.id, new BLFgroup(groupIDforFunctionKey.groupId, groupName), null, user.id === 1000);
                }
            }
        });
    },

    updateBLFofSpecificGroupforAllUsers(oldGroupName: string, group: BLFgroup, userList: AssignableBLFuser[]) {
        SupportUserService._removeSupportUserFromList(userList);
        FaxUserService._removeFaxUserFromList(userList);
        userList = userList.filter(user => user.assigned);
        this.getFunctionKeySets().then(response => {
                for (const user of userList) {
                    if (user.blfWillBeRemoved) {
                        continue;
                    }
                    this.getAllFunctionKeySettingsForUser(user.id)
                        .then(resp => {
                            const userKeys: FunctionKey[] = resp.data;
                            const filteredFunctionKeyArray = userKeys.filter(key => key.functionKeyType === 'GROUPLOGIN' && key.name === oldGroupName);
                            if (filteredFunctionKeyArray.length > 0) {
                                this.updateBLFforSpecificGroup(user.id, new BLFgroup(filteredFunctionKeyArray[0].groupIds[0], group.name), filteredFunctionKeyArray[0]);
                            }
                        });
                }
        });
    },

    deleteBLFofSpecificGroupforAllUsers(groupName: string, userList: AssignableBLFuser[]) {
        SupportUserService._removeSupportUserFromList(userList);
        FaxUserService._removeFaxUserFromList(userList);
        userList = userList.filter(user => user.assigned);
        for (const user of userList) {
            this.getAllFunctionKeySettingsForUser(user.id)
                .then(response => {
                    const userKeys: FunctionKey[] = response.data;
                    const filteredFunctionKeyArray = userKeys.filter(key => key.functionKeyType === 'GROUPLOGIN' && key.name === groupName);
                    if (filteredFunctionKeyArray.length > 0) {
                        this.deleteBLFforSpecificUser(user.id, filteredFunctionKeyArray[0].id);
                    }
                });
        }
    },
};
